import { useQuery } from '@tanstack/react-query';
import { useUserContext } from 'providers/useUserContext';
import { MindbodyClass, MindbodyWaitlistEntry, PickaspotClass } from 'types/class';
import { ClientCompleteInfo, ClientPurchases, ClientService, ClientVisit, MindbodyUser } from 'types/client';
import { GiftCard } from 'types/giftCard';
import { Service } from 'types/service';
import { MindbodySite } from 'types/site';
import { Staff } from 'types/staff';
import { MindbodyLocation } from 'types/studio';
import { ApiClientException } from 'utils/apiClientException';
import { HTTP } from 'utils/http';
import { useCoachStudioParams } from './useCoachStudioParams';
import { PaymentMethod } from 'screens/Checkout/CheckoutForm';

type UseListClassesParams = {
  week: number;
  year: number;
  studioId: number | null;
  coachId: number | null;
};

export function useListClasses(params: UseListClassesParams) {
  const { isCoachUrl, isStudioUrl, id } = useCoachStudioParams();
  if (isCoachUrl) params.coachId = id;
  if (isStudioUrl) params.studioId = id;

  const shortCodeCoachId = mondayMindbody?.coach?.length && parseInt(mondayMindbody.coach);
  if (shortCodeCoachId) params.coachId = shortCodeCoachId;

  return useQuery({
    queryKey: ['/class', JSON.stringify(params)],
    queryFn: () => HTTP.get<MindbodyClass[]>('/class', params).send(),
    staleTime: 0,
  });
}

export function useListCoaches() {
  return useQuery({ queryKey: ['coaches'], queryFn: () => HTTP.get<Staff[]>('/staff/coaches').send() });
}

export function useListPurchases() {
  const { user, isLoggedIn } = useUserContext();
  return useQuery({
    queryKey: ['/client/purchases', user?.Id],
    queryFn: () => HTTP.get<ClientPurchases[]>(`/client/purchases`).send(),
    retry: false,
    enabled: isLoggedIn,
  });
}
export function useListVisits() {
  const { user, isLoggedIn } = useUserContext();
  return useQuery({
    queryKey: ['/client/visits', user?.Id],
    queryFn: () => HTTP.get<ClientVisit[]>(`/client/visits`).send(),
    retry: false,
    enabled: isLoggedIn,
  });
}
export function useClientWaitlist() {
  const { user, isLoggedIn } = useUserContext();
  return useQuery({
    queryKey: ['/client/waitlist', user?.Id],
    queryFn: () => HTTP.get<ClientVisit[]>(`/client/waitlist`).send(),
    retry: false,
    enabled: isLoggedIn,
  });
}

export function useClientRefereralTypes() {
  return useQuery({
    queryKey: ['referalTypes'],
    queryFn: () => HTTP.get<string[]>('/client/referralTypes').send(),
    retry: false,
    gcTime: 1000 * 60 * 60,
    staleTime: 1000 * 60 * 60,
  });
}

export function useClientRequiredFields() {
  return useQuery({
    queryKey: ['/client/requiredFields'],
    queryFn: () =>
      HTTP.get<{ required_fields: string[]; custom_fields: { Id: string; Name: string; DataType: string }[] }>(
        '/client/requiredFields',
      ).send(),
    retry: false,
    gcTime: 1000 * 60 * 60,
    staleTime: 1000 * 60 * 60,
  });
}

export function useListClientServices() {
  const { user, isLoggedIn } = useUserContext();
  return useQuery({
    queryKey: ['/client/services', user?.Id],
    queryFn: () => HTTP.get<ClientService[]>(`/client/services`).send(),
    retry: false,
    enabled: isLoggedIn,
  });
}
export function useListServices(classId?: string | number) {
  return useQuery({
    queryKey: ['/sale/services', classId],
    queryFn: () => HTTP.get<Service[]>(`/sale/services`, { classId }).send(),
    retry: false,
  });
}

export function useListGiftCards(classId?: string | number) {
  return useQuery({
    queryKey: ['/sale/giftcards', classId],
    queryFn: () => HTTP.get<GiftCard[]>(`/sale/giftcard`, { classId }).send(),
    retry: false,
  });
}

export function useListLocations() {
  return useQuery({ queryKey: ['locations'], queryFn: () => HTTP.get<MindbodyLocation[]>('/site/locations').send() });
}

export function useCrossRegionalAssociations() {
  return useQuery({
    queryKey: ['/client/crossRegionalAssociations'],
    queryFn: () => HTTP.get<CrossRegionalLocationsAPIResponse>('/client/crossRegionalAssociations').send(),
  });
}

export function useListSites() {
  return useQuery({ queryKey: ['sites'], queryFn: () => HTTP.get<MindbodySite[]>('/site/sites').send() });
}

export function useFetchClientCompleteInfo() {
  const { user, isLoggedIn } = useUserContext();

  return useQuery({
    queryKey: ['/client/completeinfo', user?.Id],
    queryFn: () => HTTP.get<ClientCompleteInfo>('/client/completeinfo').send(),
    retry: false,
    enabled: isLoggedIn,
    staleTime: 1000,
  });
}

export function useFetchMe() {
  const { user, isLoggedIn } = useUserContext();

  return useQuery({
    queryKey: ['/client/me', user?.Id],
    queryFn: () => HTTP.get<MindbodyUser>('/client/me').send(),
    retry: false,
    enabled: isLoggedIn,
    staleTime: 1000,
  });
}

export function useFetchClass(id?: string, isEnrollmentInURL?: boolean) {
  const { isLoggedIn } = useUserContext();

  return useQuery<MindbodyClass, ApiClientException>({
    queryKey: ['/class', id, isEnrollmentInURL],
    queryFn: () => {
      const url = isEnrollmentInURL ? `/class/${id}/enrollment` : `/class/${id}`;
      return HTTP.get<MindbodyClass>(url).send();
    },
    retry: false,
    staleTime: 0,
    enabled: isLoggedIn && !!id,
  });
}

export function useFetchEnrollment(id?: string) {
  const { isLoggedIn } = useUserContext();

  return useQuery<MindbodyClass, ApiClientException>({
    queryKey: ['/class/enrollment', id],
    queryFn: () => HTTP.get<MindbodyClass>(`/class/enrollment/${id}`).send(),
    retry: false,
    staleTime: 0,
    enabled: isLoggedIn && !!id,
  });
}

export function useFetchClassWaitlistEntry(id?: string) {
  const { isLoggedIn } = useUserContext();

  return useQuery<MindbodyWaitlistEntry, ApiClientException>({
    queryKey: ['/class', id, '/waitlist'],
    queryFn: () => HTTP.get<MindbodyWaitlistEntry>(`/class/${id}/waitlist`).send(),
    retry: false,
    staleTime: 0,
    gcTime: 0,
    enabled: isLoggedIn && !!id,
  });
}

export function useFetchPickASpot(classId: string | number) {
  const { isLoggedIn } = useUserContext();
  return useQuery({
    queryFn: () => HTTP.get<PickaspotClass>(`/class/${classId}/pickaspot`).send(),
    queryKey: ['/class', classId, '/pickaspot'],
    staleTime: 0,
    gcTime: 0,
    retry: false,
    enabled: isLoggedIn,
  });
}

export function useFetchCustomPaymentMethods() {
  const { isLoggedIn } = useUserContext();
  return useQuery({
    queryFn: () => HTTP.get<PaymentMethod[]>(`/sale/custompaymentmethods`).send(),
    queryKey: ['/sale/custompaymentmethods'],
    staleTime: 0,
    gcTime: 0,
    retry: false,
    enabled: isLoggedIn,
  });
}

export function useFetchAvailablePaymentPlans(amount: number) {
  const { isLoggedIn } = useUserContext();
  return useQuery({
    queryFn: () => HTTP.get<PaymentMethod[]>(`/sale/almapaymentplans`, { amount }).send(),
    queryKey: ['/sale/almapaymentplans', amount],
    staleTime: 0,
    gcTime: 0,
    retry: false,
    enabled: isLoggedIn,
  });
}
